import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useUpgrade } from "@/context/UpgradeContext";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { renderMarkdown, renderBBCode, stripBBCode } from "@/lib/format";
import { Sparkles, Copy, FileCode, Loader2, Crown } from "lucide-react";

const TONES = ["Excited/Hype", "Professional/Formal", "Casual/Community"];

const PatchNotes = () => {
  const { user, checkAuth } = useAuth();
  const { openUpgrade } = useUpgrade();
  const [raw, setRaw] = useState("");
  const [tone, setTone] = useState("Excited/Hype");
  const [bbcode, setBbcode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState("");

  const isPro = !!user?.is_pro;
  const remaining = Math.max(0, (user?.free_limit ?? 3) - (user?.generations_used ?? 0));
  const limitReached = !isPro && remaining <= 0;

  const generate = async () => {
    if (!raw.trim()) {
      toast.error("Add some developer notes first.");
      return;
    }
    if (limitReached) {
      openUpgrade("You've used all 3 free generations. Upgrade to Pro for unlimited.");
      return;
    }
    setLoading(true);
    setOutput("");
    try {
      const res = await api.post("/generate/patch-notes", {
        raw_notes: raw,
        tone,
        bbcode,
      });
      setOutput(res.data.output);
      await checkAuth();
      toast.success("Patch notes generated & saved to history.");
    } catch (e) {
      if (e?.response?.status === 402) {
        await checkAuth();
        openUpgrade("You've reached your free limit. Upgrade to Pro for unlimited generations.");
      } else {
        toast.error("Generation failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    } catch {
      toast.error("Couldn't access the clipboard.");
    }
  };

  return (
    <Layout title="Steam Patch Notes & Changelog Generator" subtitle="Tool 01 // Turn raw notes into polished updates">
      {limitReached && (
        <div
          className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-teal-500/30 bg-teal-400/10 px-5 py-4"
          data-testid="free-limit-banner"
        >
          <p className="text-sm text-zinc-100 flex items-center gap-2">
            <Crown className="h-4 w-4 text-teal-300 shrink-0" />
            You've reached your free limit. Upgrade to Pro for unlimited generations.
          </p>
          <button
            onClick={() => openUpgrade("Get unlimited generations across all 3 tools.")}
            data-testid="banner-upgrade-btn"
            className="shrink-0 bg-teal-400 text-zinc-950 font-semibold text-sm py-2 px-4 rounded-xl hover:bg-teal-300 transition-colors duration-200"
          >
            Upgrade to Pro
          </button>
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-6" data-testid="patch-notes-module">
        {/* Input */}
        <div className="border border-zinc-800 bg-zinc-900/30 rounded-2xl p-5 flex flex-col">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Input</p>
          <label className="text-sm text-zinc-300 mb-2">Raw dev notes, git commits, or bullet points</label>
          <Textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            data-testid="patch-notes-input"
            placeholder={"- fixed crash when loading save on boss arena\n- added 3 new weapons\n- rebalanced final boss HP\n- git: perf pass on lighting"}
            className="min-h-[220px] bg-zinc-950 border-zinc-800 rounded-xl font-mono text-sm text-zinc-200 focus:border-teal-400 focus:ring-1 focus:ring-teal-400 resize-y"
          />

          <div className="grid sm:grid-cols-2 gap-4 mt-5">
            <div>
              <label className="text-sm text-zinc-300 mb-2 block">Tone</label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger data-testid="tone-select" className="bg-zinc-950 border-zinc-800 rounded-xl focus:border-teal-400 focus:ring-teal-400">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 rounded-xl">
                  {TONES.map((t) => (
                    <SelectItem key={t} value={t} className="focus:bg-zinc-800 focus:text-teal-400 rounded-xl" data-testid={`tone-option-${t}`}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-3 cursor-pointer bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2.5 w-full hover:border-zinc-700 transition-colors duration-200">
                <Checkbox
                  checked={bbcode}
                  onCheckedChange={(v) => setBbcode(!!v)}
                  data-testid="bbcode-checkbox"
                  className="border-zinc-600 data-[state=checked]:bg-teal-400 data-[state=checked]:border-teal-400 data-[state=checked]:text-zinc-950 rounded-xl"
                />
                <span className="text-sm text-zinc-300 flex items-center gap-1.5">
                  <FileCode className="h-4 w-4 text-teal-400" /> Steam BBCode
                </span>
              </label>
            </div>
          </div>

          <button
            onClick={generate}
            disabled={loading || limitReached}
            data-testid="generate-patch-notes-btn"
            className="mt-5 flex items-center justify-center gap-2 bg-teal-400 text-zinc-950 font-semibold py-3 rounded-xl hover:bg-teal-300 transition-colors duration-200 disabled:bg-zinc-800 disabled:text-zinc-500 disabled:cursor-not-allowed active:scale-[0.99]"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Generating…
              </>
            ) : limitReached ? (
              <>
                <Crown className="h-4 w-4" /> Free Limit Reached
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" /> Generate Patch Notes
              </>
            )}
          </button>
          {!isPro && !limitReached && (
            <p className="mt-2 text-center text-xs text-zinc-500 font-mono" data-testid="patch-notes-remaining">
              {remaining} of {user?.free_limit ?? 3} free generations left
            </p>
          )}
        </div>

        {/* Output */}
        <div className="border border-zinc-800 bg-zinc-900/30 rounded-2xl p-5 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              Preview {bbcode ? "// BBCode" : "// Markdown"}
            </p>
            {output && (
              <div className="flex items-center gap-2">
                {bbcode && (
                  <button
                    onClick={() => copy(output, "BBCode")}
                    data-testid="copy-bbcode-btn"
                    className="flex items-center gap-1.5 text-xs border border-zinc-700 text-teal-400 rounded-xl px-3 py-1.5 hover:border-teal-400 hover:bg-teal-400/10 transition-colors duration-200"
                  >
                    <Copy className="h-3.5 w-3.5" /> Copy BBCode
                  </button>
                )}
                <button
                  onClick={() => copy(bbcode ? stripBBCode(output) : output, "Raw text")}
                  data-testid="copy-raw-btn"
                  className="flex items-center gap-1.5 text-xs border border-zinc-700 text-zinc-300 rounded-xl px-3 py-1.5 hover:border-teal-400 hover:text-teal-400 transition-colors duration-200"
                >
                  <Copy className="h-3.5 w-3.5" /> Copy Raw Text
                </button>
              </div>
            )}
          </div>

          <div className="flex-1 bg-zinc-950 border border-zinc-800 rounded-xl p-4 overflow-auto min-h-[300px]" data-testid="patch-notes-output">
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-5 w-1/3 bg-zinc-800 rounded-xl" />
                <Skeleton className="h-4 w-full bg-zinc-800 rounded-xl" />
                <Skeleton className="h-4 w-5/6 bg-zinc-800 rounded-xl" />
                <Skeleton className="h-5 w-1/4 bg-zinc-800 rounded-xl mt-4" />
                <Skeleton className="h-4 w-full bg-zinc-800 rounded-xl" />
                <Skeleton className="h-4 w-2/3 bg-zinc-800 rounded-xl" />
              </div>
            ) : output ? (
              bbcode ? (
                <div className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: renderBBCode(output) }} />
              ) : (
                <div className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: renderMarkdown(output) }} />
              )
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <FileCode className="h-8 w-8 text-zinc-700 mb-3" />
                <p className="text-zinc-600 text-sm font-mono">Your generated patch notes will appear here.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PatchNotes;
